import {
  CustomerData,
  ShippingData,
  BillingData,
  CartGroup,
  OrderPayload,
} from "../types";

// Enhanced error type with field ID for scrolling and focus management
export interface ValidationError {
  message: string;
  fieldId: string; // ID of the field with error
}

// Enhanced validation result type
export interface ValidationResult {
  errors: Record<string, ValidationError>;
  hasErrors: boolean;
  firstErrorField: string | null;
}

// Validate order data
export const validateOrderData = (data: any) => {
  console.log("Data", data);
  const errors: any = {};
  if (!data.customerInfo.firstName)
    errors.firstName = "Customer name is required.";
  if (!data.customerInfo.lastName)
    errors.lastName = "Customer name is required.";
  if (!data.customerInfo.email) errors.email = "Email is required.";
  if (!data.paymentMethod) errors.paymentMethod = "Payment method is required.";
  if (!data.deliveryAddress.address)
    errors.address = "Delivery address is required.";
  return errors;
};

// Validation for shipping data
export const validateShippingData = (data: any): ValidationResult => {
  const errors: Record<string, ValidationError> = {};

  if (!data.email || !data.email.includes("@")) {
    errors.email = {
      message: "Por favor, ingrese un correo electrónico válido.",
      fieldId: "email",
    };
  }

  if (!data.country) {
    errors.country = {
      message: "Por favor, seleccione un país.",
      fieldId: "country",
    };
  }

  if (!data.department) {
    errors.department = {
      message: "Por favor, seleccione un departamento.",
      fieldId: "department",
    };
  }

  if (!data.province) {
    errors.province = {
      message: "Por favor, seleccione una provincia.",
      fieldId: "province",
    };
  }

  if (!data.district) {
    errors.district = {
      message: "Por favor, seleccione un distrito.",
      fieldId: "district",
    };
  }

  if (!data.address) {
    errors.address = {
      message: "Por favor, ingrese una dirección válida.",
      fieldId: "address",
    };
  }

  const firstErrorField = Object.keys(errors)[0] || null;

  return {
    errors,
    hasErrors: Object.keys(errors).length > 0,
    firstErrorField,
  };
};

// Validation for customer data
export const validateCustomerData = (data: any): ValidationResult => {
  const errors: Record<string, ValidationError> = {};

  if (!data.firstName || data.firstName.trim().length === 0) {
    errors.firstName = {
      message: "El nombre es obligatorio.",
      fieldId: "firstName",
    };
  }

  if (!data.lastName || data.lastName.trim().length === 0) {
    errors.lastName = {
      message: "El apellido es obligatorio.",
      fieldId: "lastName",
    };
  }

  if (!data.phone || data.phone.trim().length < 9) {
    errors.phone = {
      message: "El número de celular debe tener al menos 9 dígitos.",
      fieldId: "phone",
    };
  }

  if (!data.documentType) {
    errors.documentType = {
      message: "Por favor, seleccione un tipo de documento.",
      fieldId: "documentType",
    };
  }

  if (!data.documentNumber || data.documentNumber.trim().length < 8) {
    errors.documentNumber = {
      message: "El número de documento debe tener al menos 8 caracteres.",
      fieldId: "documentNumber",
    };
  }

  if (data.receiverDifferent) {
    if (!data.receiverName || data.receiverName.trim().length === 0) {
      errors.receiverName = {
        message: "El nombre de quien recibe es obligatorio.",
        fieldId: "receiverName",
      };
    }

    if (
      !data.receiverDocumentType ||
      !["DNI", "Pasaporte", "Carnet de Extranjería"].includes(
        data.receiverDocumentType
      )
    ) {
      errors.receiverDocumentType = {
        message:
          "Por favor, seleccione un tipo de documento válido para quien recibe.",
        fieldId: "receiverDocumentType",
      };
    }

    if (
      !data.receiverDocumentNumber ||
      data.receiverDocumentNumber.trim().length < 8
    ) {
      errors.receiverDocumentNumber = {
        message:
          "El número de documento de quien recibe debe tener al menos 8 caracteres.",
        fieldId: "receiverDocumentNumber",
      };
    }

    if (!data.receiverPhone || data.receiverPhone.trim().length < 8) {
      errors.receiverPhone = {
        message: "El celular de quien recibe debe tener al menos 8 caracteres.",
        fieldId: "receiverPhone",
      };
    }
  }

  if (data.invoiceRequired) {
    if (!data.ruc || data.ruc.trim().length !== 11) {
      errors.ruc = {
        message: "El RUC debe tener 11 dígitos.",
        fieldId: "ruc",
      };
    }

    if (!data.razonSocial || data.razonSocial.trim().length === 0) {
      errors.razonSocial = {
        message: "La razón social es obligatoria.",
        fieldId: "razonSocial",
      };
    }

    if (!data.direccionFiscal || data.direccionFiscal.trim().length === 0) {
      errors.direccionFiscal = {
        message: "La dirección fiscal es obligatoria.",
        fieldId: "direccionFiscal",
      };
    }
  }

  const firstErrorField = Object.keys(errors)[0] || null;

  return {
    errors,
    hasErrors: Object.keys(errors).length > 0,
    firstErrorField,
  };
};

// Validation for billing data
export const validateBillingData = (data: any): ValidationResult => {
  const errors: Record<string, ValidationError> = {};

  if (!data.paymentMethod) {
    errors.paymentMethod = {
      message: "Debe seleccionar un método de pago",
      fieldId: "paymentMethod",
    };
  }

  const firstErrorField = Object.keys(errors)[0] || null;

  return {
    errors,
    hasErrors: Object.keys(errors).length > 0,
    firstErrorField,
  };
};

export function prepareOrderPayload({
  customerData,
  shippingData,
  billingData,
  cartGroups,
}: {
  customerData: CustomerData;
  shippingData: ShippingData;
  billingData: BillingData;
  cartGroups: CartGroup[];
}): OrderPayload {
  return {
    receiverInfo: {
      name: customerData.receiverName || "",
      documentType: customerData.receiverDocumentType || "",
      documentNumber: customerData.receiverDocumentNumber || "",
      phone: customerData.receiverPhone || "",
    },
    customerInfo: {
      firstName: customerData.firstName || "",
      lastName: customerData.lastName || "",
      email: shippingData.email || "",
      phone: customerData.phone || "",
      documentType: customerData.documentType || "",
      documentNumber: customerData.documentNumber || "",
      orderComments: shippingData.orderComments || "",
    },
    billingInfo: {
      paymentMethod: billingData.paymentMethod || "",
      paymeResponse: billingData.paymeResponse || "",
      receiptType: customerData.receiptType || "",
      ruc: customerData.ruc || "",
      razonSocial: customerData.razonSocial || "",
      direccionFiscal: customerData.direccionFiscal || "",
    },
    userAddress: {
      country: shippingData.country || "",
      department: shippingData.department || "",
      province: shippingData.province || "",
      reference: shippingData.reference || "",
      district: shippingData.district || "",
      address: shippingData.address || "",
      ubigeo: shippingData.ubigeoCode || "",
      phone: customerData.phone || "",
      name: `${customerData.firstName || ""} ${
        customerData.lastName || ""
      }`.trim(),
    },

    groups: cartGroups.map((group) => ({
      id: group.id,
      subTotal: group.groupTotals?.finalSubtotal || group.subTotal || 0,
      deliveryFee:
        group.groupTotals?.deliveryFeeTotal || group.deliveryFee || 0,
    })),
    totals: {
      totalPrice: cartGroups.reduce(
        (sum: number, group: CartGroup) =>
          sum + (group.groupTotals?.finalSubtotal || group.subTotal || 0),
        0
      ),
      totalDeliveryFee: cartGroups.reduce(
        (sum: number, group: CartGroup) =>
          sum + (group.groupTotals?.deliveryFeeTotal || group.deliveryFee || 0),
        0
      ),
    },
  };
}
